import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import {
  Box, Button, Chip, CircularProgress, IconButton, Stack,
  Switch, Tooltip, Typography,
} from '@mui/material'
import { FormInput } from '../components/ui'
import {
  Add as AddIcon,
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  EmojiEvents as WonIcon,
  Cancel as LostIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material'
import { toast } from 'react-toastify'
import { dealsApi, PipelineStage } from '../lib/api/dealsApi'
import { PageHeader } from '../components/shared/PageHeader'
import { EntityModal } from '../components/shared/EntityModal'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { themeColors } from '../app/layout/theme'
import { authStore } from '../lib/stores/authStore'
import { usePermission } from '../lib/hooks/usePermission'

// ── Colour palette ────────────────────────────────────────────────────────────

const PRESET_COLORS = [
  '#6366f1', '#3b82f6', '#0ea5e9', '#14b8a6',
  '#16a34a', '#84cc16', '#eab308', '#f97316',
  '#ef4444', '#ec4899', '#7c3aed', '#64748b',
]

function ColorDot({ color, size = 20 }: { color: string; size?: number }) {
  return <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
}

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <Box>
      <Typography fontSize="12px" color="text.secondary" fontWeight={600} mb={1}>Stage Colour</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {PRESET_COLORS.map(c => (
          <Box key={c} onClick={() => onChange(c)} sx={{
            width: 28, height: 28, borderRadius: '50%', bgcolor: c, cursor: 'pointer',
            border: value === c ? '3px solid #1d4ed8' : '2px solid transparent',
            outline: value === c ? '2px solid #fff' : 'none',
            boxShadow: value === c ? `0 0 0 3px ${c}60` : 'none',
            transition: 'all 0.15s',
            '&:hover': { transform: 'scale(1.18)' },
          }} />
        ))}
      </Box>
    </Box>
  )
}

// ── Stage Form Modal (Add / Edit) ─────────────────────────────────────────────

interface StageFormValues {
  name:            string
  color:           string
  probabilityPct:  string
  isWon:           boolean
  isLost:          boolean
  autoTaskTitle:   string
  autoTaskDueDays: string
}

function StageFormModal({ open, onClose, stage, nextPosition }: {
  open:         boolean
  onClose:      () => void
  stage?:       PipelineStage   // undefined = create mode
  nextPosition: number
}) {
  const qc = useQueryClient()
  const isEdit = !!stage

  const [form, setForm] = useState<StageFormValues>(() => ({
    name:            stage?.name            ?? '',
    color:           stage?.color           ?? '#6366f1',
    probabilityPct:  stage?.probabilityPct  != null ? String(stage.probabilityPct) : '',
    isWon:           stage?.isWon           ?? false,
    isLost:          stage?.isLost          ?? false,
    autoTaskTitle:   stage?.autoTaskTitle   ?? '',
    autoTaskDueDays: stage?.autoTaskDueDays != null ? String(stage.autoTaskDueDays) : '1',
  }))
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)

  const set = (k: keyof StageFormValues, v: string | boolean) =>
    setForm(f => ({ ...f, [k]: v }))

  // isWon / isLost are mutually exclusive
  const setWon  = (v: boolean) => setForm(f => ({ ...f, isWon: v, isLost: v ? false : f.isLost }))
  const setLost = (v: boolean) => setForm(f => ({ ...f, isLost: v, isWon: v ? false : f.isWon }))

  const createMut = useMutation({
    mutationFn: () => dealsApi.createStage({
      name:            form.name.trim(),
      position:        nextPosition,
      color:           form.color,
      probabilityPct:  form.probabilityPct ? parseInt(form.probabilityPct) : undefined,
      isWon:           form.isWon,
      isLost:          form.isLost,
      autoTaskTitle:   form.autoTaskTitle.trim() || undefined,
      autoTaskDueDays: form.autoTaskTitle.trim() && form.autoTaskDueDays ? parseInt(form.autoTaskDueDays) : undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline-stages'] })
      toast.success('Stage created')
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to create stage'),
  })

  const updateMut = useMutation({
    mutationFn: () => dealsApi.updateStage(stage!.id, {
      name:            form.name.trim(),
      position:        stage!.position,
      color:           form.color,
      probabilityPct:  form.probabilityPct ? parseInt(form.probabilityPct) : null,
      isWon:           form.isWon,
      isLost:          form.isLost,
      autoTaskTitle:   form.autoTaskTitle.trim() || null,
      autoTaskDueDays: form.autoTaskTitle.trim() && form.autoTaskDueDays ? parseInt(form.autoTaskDueDays) : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline-stages'] })
      toast.success('Stage updated')
      onClose()
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update stage'),
  })

  const isPending = createMut.isPending || updateMut.isPending

  const handleSubmit = () => {
    if (isEdit) {
      setIsSaveConfirmOpen(true)
    } else {
      createMut.mutate()
    }
  }

  const handleConfirmSave = () => {
    setIsSaveConfirmOpen(false)
    updateMut.mutate()
  }

  return (
    <>
      <EntityModal
        open={open}
        onClose={onClose}
        onSubmit={handleSubmit}
        title={isEdit ? 'Edit Stage' : 'New Stage'}
        submitLabel={isEdit ? 'Save Changes' : 'Create Stage'}
        isSubmitting={isPending}
        submitDisabled={!form.name.trim()}
        maxWidth="xs"
        icon={isEdit ? <EditIcon sx={{ fontSize: 18 }} /> : <AddIcon sx={{ fontSize: 18 }} />}
      >
        <Stack spacing={2.5}>
          {/* Name */}
          <FormInput
            label="Stage Name *"
            value={form.name}
            onChange={e => set('name', e.target.value)}
            fullWidth
            placeholder="e.g. Proposal"
            autoFocus
          />

          {/* Colour picker */}
          <Box>
            <ColorPicker value={form.color} onChange={c => set('color', c)} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
              <ColorDot color={form.color} />
              <Typography fontSize="12px" color="text.secondary">Preview</Typography>
              <Chip label={form.name || 'Stage Name'} size="small" sx={{
                bgcolor: `${form.color}22`, color: form.color,
                fontWeight: 700, fontSize: '11px', borderRadius: '4px',
                border: `1px solid ${form.color}44`,
              }} />
            </Box>
          </Box>

          {/* Probability */}
          <FormInput
            label="Win Probability %"
            value={form.probabilityPct}
            onChange={e => set('probabilityPct', e.target.value)}
            type="number"
            fullWidth
            placeholder="e.g. 60"
            inputProps={{ min: 0, max: 100 }}
            helperText="Auto-fills probability when this stage is selected in a deal"
          />

          {/* Auto-task on enter */}
          <Box sx={{ p: 1.5, borderRadius: '10px', border: `1px solid ${themeColors.border}`, bgcolor: '#fafafa' }}>
            <Typography fontSize="12px" fontWeight={600} color="text.secondary" mb={0.5}>
              Auto-Task on Stage Enter
            </Typography>
            <Typography fontSize="11px" color="text.disabled" mb={1.5}>
              Automatically create a task whenever a deal moves into this stage
            </Typography>
            <Stack spacing={1.5}>
              <FormInput
                label="Task Title (leave blank to disable)"
                value={form.autoTaskTitle}
                onChange={e => set('autoTaskTitle', e.target.value)}
                fullWidth
                placeholder="e.g. Send proposal to client"
              />
              {form.autoTaskTitle.trim() && (
                <FormInput
                  label="Due in (days)"
                  value={form.autoTaskDueDays}
                  onChange={e => set('autoTaskDueDays', e.target.value)}
                  type="number"
                  fullWidth
                  inputProps={{ min: 1, max: 365 }}
                  helperText="Number of days from today when the task is due"
                />
              )}
            </Stack>
          </Box>

          {/* Terminal flags */}
          <Box sx={{ p: 1.5, borderRadius: '10px', border: `1px solid ${themeColors.border}`, bgcolor: '#fafafa' }}>
            <Typography fontSize="12px" fontWeight={600} color="text.secondary" mb={1.5}>
              Terminal Stage
            </Typography>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WonIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                  <Box>
                    <Typography fontSize="13px" fontWeight={600}>Won</Typography>
                    <Typography fontSize="11px" color="text.secondary">Deals moved here are marked as Won</Typography>
                  </Box>
                </Box>
                <Switch
                  size="small"
                  checked={form.isWon}
                  onChange={e => setWon(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#16a34a' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#16a34a' } }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LostIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                  <Box>
                    <Typography fontSize="13px" fontWeight={600}>Lost</Typography>
                    <Typography fontSize="11px" color="text.secondary">Deals moved here are marked as Lost</Typography>
                  </Box>
                </Box>
                <Switch
                  size="small"
                  checked={form.isLost}
                  onChange={e => setLost(e.target.checked)}
                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#dc2626' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#dc2626' } }}
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </EntityModal>

      <ConfirmDialog
        open={isSaveConfirmOpen}
        title="Save Changes"
        description="Are you sure you want to save the changes to this pipeline stage?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

// ── Stage Row ─────────────────────────────────────────────────────────────────

function StageRow({
  stage, onEdit, onDelete, canEdit, canDelete,
  onDragStart, onDragEnd, onDragOver, onDrop, isDragging
}: {
  stage:       PipelineStage
  onEdit:      (s: PipelineStage) => void
  onDelete:    (s: PipelineStage) => void
  canEdit:     boolean
  canDelete:   boolean
  onDragStart: (e: React.DragEvent) => void
  onDragEnd:   (e: React.DragEvent) => void
  onDragOver:  (e: React.DragEvent) => void
  onDrop:      (e: React.DragEvent) => void
  isDragging:  boolean
}) {
  return (
    <Box
      draggable={canEdit}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      sx={{
        display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5,
        border: `1px solid ${themeColors.border}`, borderRadius: '10px', bgcolor: '#fff',
        transition: 'all 0.15s',
        opacity: isDragging ? 0.4 : 1,
        cursor: canEdit ? 'grab' : 'default',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderColor: '#cbd5e1' },
      }}
    >
      {/* Drag handle */}
      <DragIcon sx={{ fontSize: 18, color: '#d1d5db', flexShrink: 0 }} />

      {/* Position badge */}
      <Box sx={{
        width: 24, height: 24, borderRadius: '50%', bgcolor: '#f1f5f9',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Typography fontSize="11px" fontWeight={700} color="text.secondary">{stage.position}</Typography>
      </Box>

      {/* Color dot + name */}
      <ColorDot color={stage.color} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={stage.name} size="small" sx={{
            bgcolor: `${stage.color}22`, color: stage.color,
            fontWeight: 700, fontSize: '12px', borderRadius: '6px',
            border: `1px solid ${stage.color}44`, height: 24,
            '& .MuiChip-label': { px: '8px' },
          }} />
          {stage.isWon && (
            <Chip icon={<WonIcon sx={{ fontSize: 12, '&.MuiChip-icon': { color: '#16a34a' } }} />}
              label="Won" size="small" sx={{ height: 20, fontSize: '10px', fontWeight: 700,
                bgcolor: '#f0fdf4', color: '#16a34a', borderRadius: '4px', '& .MuiChip-label': { pl: '2px', pr: '6px' } }} />
          )}
          {stage.isLost && (
            <Chip icon={<LostIcon sx={{ fontSize: 12, '&.MuiChip-icon': { color: '#dc2626' } }} />}
              label="Lost" size="small" sx={{ height: 20, fontSize: '10px', fontWeight: 700,
                bgcolor: '#fef2f2', color: '#dc2626', borderRadius: '4px', '& .MuiChip-label': { pl: '2px', pr: '6px' } }} />
          )}
          {stage.autoTaskTitle && (
            <Chip
              label={`⚡ ${stage.autoTaskTitle}`}
              size="small"
              sx={{ height: 20, fontSize: '10px', fontWeight: 600,
                bgcolor: '#f0fdf4', color: '#16a34a', borderRadius: '4px',
                '& .MuiChip-label': { px: '6px' }, maxWidth: 180 }}
            />
          )}
        </Box>
      </Box>

      {/* Probability */}
      {stage.probabilityPct != null ? (
        <Typography fontSize="12.5px" fontWeight={600} color="text.secondary" sx={{ flexShrink: 0 }}>
          {stage.probabilityPct}%
        </Typography>
      ) : (
        <Typography fontSize="11px" color="text.disabled" sx={{ flexShrink: 0 }}>—</Typography>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
        {canEdit && (
          <Tooltip title="Edit stage">
            <IconButton size="small" onClick={() => onEdit(stage)} sx={{ color: '#9ca3af', '&:hover': { color: themeColors.primary, bgcolor: '#F2EEF9' } }}>
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete stage">
            <IconButton size="small" onClick={() => onDelete(stage)} sx={{ color: '#9ca3af', '&:hover': { color: '#dc2626' } }}>
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PipelineStagesPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { hasAction, isSuperAdmin } = usePermission()
  const canCreate    = hasAction('/settings', 'create') || isSuperAdmin
  const canEdit      = hasAction('/settings', 'update') || isSuperAdmin
  const canDelete    = hasAction('/settings', 'delete') || isSuperAdmin

  const [addOpen,    setAddOpen]    = useState(false)
  const [editStage,  setEditStage]  = useState<PipelineStage | null>(null)
  const [deleteStage, setDeleteStage] = useState<PipelineStage | null>(null)

  const { data: stages = [], isLoading } = useQuery({
    queryKey: ['pipeline-stages'],
    queryFn:  () => dealsApi.getStages(),
    staleTime: 30_000,
  })

  const deleteMut = useMutation({
    mutationFn: (id: string) => dealsApi.deleteStage(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline-stages'] })
      toast.success('Stage deleted')
      setDeleteStage(null)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete stage'),
  })

  const [localStages, setLocalStages] = useState<PipelineStage[]>([])
  const [draggedStageId, setDraggedStageId] = useState<string | null>(null)
  const [isReorderConfirmOpen, setIsReorderConfirmOpen] = useState(false)

  useEffect(() => {
    if (stages) {
      setLocalStages([...stages].sort((a, b) => a.position - b.position))
    }
  }, [stages])

  const reorderMutation = useMutation({
    mutationFn: async (newStages: PipelineStage[]) => {
      const updates = newStages.map((stage, idx) => {
        const newPos = idx + 1
        const isFallbackId = stage.id.startsWith('fallback-')
        const originalStage = stages.find(s => s.id === stage.id)
        const originalPos = originalStage ? originalStage.position : -1
        if (!isFallbackId && originalPos !== newPos) {
          return dealsApi.updateStage(stage.id, {
            name: stage.name,
            position: newPos,
            color: stage.color,
            probabilityPct: stage.probabilityPct,
            isWon: stage.isWon,
            isLost: stage.isLost,
            autoTaskTitle: stage.autoTaskTitle,
            autoTaskDueDays: stage.autoTaskDueDays,
          })
        }
        return null
      }).filter((p): p is Promise<PipelineStage[]> => p !== null)
      
      await Promise.all(updates)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pipeline-stages'] })
      toast.success('Stages reordered successfully')
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.message ?? 'Failed to reorder stages')
      if (stages) {
        setLocalStages([...stages].sort((a, b) => a.position - b.position))
      }
    }
  })

  const hasChanges = useMemo(() => {
    if (stages.length !== localStages.length) return false
    const sortedOriginal = [...stages].sort((a, b) => a.position - b.position)
    return localStages.some((s, idx) => s.id !== sortedOriginal[idx]?.id)
  }, [stages, localStages])

  const handleDiscardChanges = () => {
    if (stages) {
      setLocalStages([...stages].sort((a, b) => a.position - b.position))
    }
  }

  const handleConfirmSaveOrder = () => {
    setIsReorderConfirmOpen(false)
    reorderMutation.mutate(localStages)
  }

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDraggedStageId(id)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnd = () => {
    setDraggedStageId(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault()
    if (!draggedStageId || draggedStageId === targetId) return

    const draggedStage = localStages.find(s => s.id === draggedStageId)
    const targetStage = localStages.find(s => s.id === targetId)
    if (!draggedStage || !targetStage) return

    const isDraggedTerminal = draggedStage.isWon || draggedStage.isLost
    const isTargetTerminal = targetStage.isWon || targetStage.isLost
    if (isDraggedTerminal !== isTargetTerminal) return

    const currentGroup = isDraggedTerminal
      ? localStages.filter(s => s.isWon || s.isLost)
      : localStages.filter(s => !s.isWon && !s.isLost)

    const otherGroup = isDraggedTerminal
      ? localStages.filter(s => !s.isWon && !s.isLost)
      : localStages.filter(s => s.isWon || s.isLost)

    const draggedIndex = currentGroup.findIndex(s => s.id === draggedStageId)
    const targetIndex = currentGroup.findIndex(s => s.id === targetId)

    const reorderedGroup = [...currentGroup]
    reorderedGroup.splice(draggedIndex, 1)
    reorderedGroup.splice(targetIndex, 0, draggedStage)

    const updatedActive = isDraggedTerminal ? otherGroup : reorderedGroup
    const updatedTerminal = isDraggedTerminal ? reorderedGroup : otherGroup

    const finalStages: PipelineStage[] = []
    let currentPos = 1
    updatedActive.forEach(s => {
      finalStages.push({ ...s, position: currentPos++ })
    })
    updatedTerminal.forEach(s => {
      finalStages.push({ ...s, position: currentPos++ })
    })

    setLocalStages(finalStages)
  }

  const activeStages   = localStages.filter(s => !s.isWon && !s.isLost)
  const terminalStages = localStages.filter(s => s.isWon || s.isLost)
  const nextPosition   = localStages.length > 0 ? Math.max(...localStages.map(s => s.position)) + 1 : 1

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Deal Stages"
        breadcrumb={[{ label: 'Settings', to: '/settings' }]}
        showBreadcrumb
        actions={canCreate ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px',
              bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primaryDark } }}
          >
            Add Stage
          </Button>
        ) : undefined}
      />

      <Box sx={{ flex: 1, px: 3, pb: 4, maxWidth: 680, overflowY: 'auto' }}>
        {/* Info banner */}
        <Box sx={{
          p: 2, mb: 3, borderRadius: '10px',
          bgcolor: '#eff6ff', border: '1px solid #bfdbfe',
          display: 'flex', gap: 1.5,
        }}>
          <Typography fontSize="13px" color="#1d4ed8" sx={{ lineHeight: 1.6 }}>
            Stages define the steps in your sales pipeline. Each deal moves through these stages toward a Won or Lost outcome.
            Set a probability % to auto-fill deal win probability when a stag is selected
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : stages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#fff', borderRadius: '14px',
            border: `2px dashed ${themeColors.border}` }}>
            <Typography fontSize="15px" fontWeight={700} color="text.primary" mb={0.5}>No stages yet</Typography>
            <Typography fontSize="13px" color="text.secondary" mb={3}>
              Create your first pipeline stage to start tracking deals
            </Typography>
            {canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}
                sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700,
                  bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primaryDark } }}>
                Add Stage
              </Button>
            )}
          </Box>
        ) : (
          <Stack spacing={3}>
            {/* Active stages */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography fontSize="12px" fontWeight={700} color="text.secondary"
                  textTransform="uppercase" letterSpacing="0.5px">
                  Active Stages ({activeStages.length})
                </Typography>
                <Typography fontSize="11px" color="text.disabled">Probability</Typography>
              </Box>
              {activeStages.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', bgcolor: '#f9fafb', borderRadius: '10px', border: '1px dashed #e5e7eb' }}>
                  <Typography fontSize="13px" color="text.secondary">No active stages — add one above</Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {activeStages.map(s => (
                    <StageRow key={s.id} stage={s}
                      onEdit={setEditStage}
                      onDelete={setDeleteStage}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onDragStart={(e) => handleDragStart(e, s.id)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, s.id)}
                      isDragging={s.id === draggedStageId}
                    />
                  ))}
                </Stack>
              )}
            </Box>

            {/* Terminal stages */}
            {terminalStages.length > 0 && (
              <Box>
                <Typography fontSize="12px" fontWeight={700} color="text.secondary"
                  textTransform="uppercase" letterSpacing="0.5px" sx={{ mb: 1.5 }}>
                  Terminal Stages ({terminalStages.length})
                </Typography>
                <Stack spacing={1}>
                  {terminalStages.map(s => (
                    <StageRow key={s.id} stage={s}
                      onEdit={setEditStage}
                      onDelete={setDeleteStage}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onDragStart={(e) => handleDragStart(e, s.id)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, s.id)}
                      isDragging={s.id === draggedStageId}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* Add modal */}
      {addOpen && (
        <StageFormModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          nextPosition={nextPosition}
        />
      )}

      {/* Edit modal */}
      {editStage && (
        <StageFormModal
          open={!!editStage}
          onClose={() => setEditStage(null)}
          stage={editStage}
          nextPosition={nextPosition}
        />
      )}

      {/* Delete dialog */}
      <DeleteDialog
        open={!!deleteStage}
        onClose={() => setDeleteStage(null)}
        onConfirm={() => deleteStage && deleteMut.mutate(deleteStage.id)}
        isDeleting={deleteMut.isPending}
        title="Delete Stage"
        description={`Are you sure you want to delete "${deleteStage?.name}"? Deals in this stage will not be deleted but will lose their stage assignment.`}
      />

      {/* Save Reorder Confirmation */}
      <ConfirmDialog
        open={isReorderConfirmOpen}
        title="Save Stage Order"
        description="Are you sure you want to save the new order of pipeline stages?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsReorderConfirmOpen(false)}
        onConfirm={handleConfirmSaveOrder}
      />

      {/* Bottom Floating Action Buttons (No wrapping background/border box) */}
      {hasChanges && (
        <Box sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: 'flex',
          gap: 1.5,
          zIndex: 1000,
        }}>
          <Button
            variant="outlined"
            onClick={handleDiscardChanges}
            disabled={reorderMutation.isPending}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontWeight: 600, fontSize: '13px',
              bgcolor: '#fff', color: '#374151', borderColor: '#d1d5db',
              '&:hover': { bgcolor: '#f9fafb', borderColor: '#cbd5e1' }
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            onClick={() => setIsReorderConfirmOpen(true)}
            disabled={reorderMutation.isPending}
            sx={{
              borderRadius: '8px', textTransform: 'none', fontWeight: 700, fontSize: '13px',
              bgcolor: themeColors.primary, '&:hover': { bgcolor: themeColors.primaryDark }
            }}
          >
            {reorderMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      )}
    </Box>
  )
}
